// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import { get } from 'lodash';
import { convertHexToRGB } from '../../../../utils';

const styles = ({ color, fontSize, typography, margin }) => ({
  appConfigurationContainer: {
    '& table': {
      backgroundColor: get(color, 'uiBackground'),
    },
    '& tr': {
      borderTop: `1px solid ${get(color, 'borderColor')}`,
    },
    '& th': {
      verticalAlign: 'middle',
      display: 'table-cell',
      color: get(color, 'bodyText'),
      paddingTop: 2,
      paddingBottom: 2,
      cursor: 'pointer',
      border: 'none',
      '&:first-child' : {
        color: get(color, 'primaryButtonBackground'),
        paddingLeft: 36,
      },
      '&:last-child' : {
        paddingRight: 36,
      },
      height: 40,
    },
    '& td': {
      verticalAlign: 'middle',
      padding: 12,
      border: 'none',
    },
  },
  paperClass: {
    padding: 32,
    paddingTop: 0,
    maxHeight: '100%',
    maxWidth: '100%',
    width: '80%',
    height: '90%',
  },
  editKeyPaperClass: {
    padding: 32,
    paddingTop: 0,
    height: 'calc(100% - 200px)',
    width: 750,
  },
  secretDialogPaperClass: {
    padding: 32,
    paddingTop: 0,
    width: 560,
    //height: '90%',
  },
  generateSecretDialog: {
    fontFamily: `${get(typography, 'bodyText')} !important`,
    color: get(color, 'bodyText'),
    margin: 20,
    width: 500,
  },
  errorBackgroundButton: {
    backgroundColor: get(color, 'error'),
    color: get(color, 'bodyBackground'),
    '&:hover': {
      color: get(color, 'bodyBackground'),
      backgroundColor: get(color, 'error'),
    },
    marginRight: 20,
  },
  expiryDateText: {
    fontFamily: 'clear-sans-bold',
  },
  expiredKeyStatus: {
    color: '#B30303',
    fontFamily: 'clear-sans-bold',
  },
  cancelButton: {
    '&:focus': {
      outlineWidth: '1px !important',
      outlineOffset: '-4px !important',
      outlineStyle: 'solid !important',
      outlineRadius: '3px !important',
    },
  },
  keyListKeyName: {
    verticalAlign: 'middle',
    marginLeft: 4,
  },
  dialogButtonPanel: {
    marginTop: 30,
  },
  certOrKeyIcon: {
    verticalAlign: 'middle',
    width: 20,
    height: 20,
    marginTop: 3,
  },
  generateSecretDialogContent: {
    fontSize: `${get(fontSize, 'bodyText')} !important`,
  },
  
  button: {
    color: get(color, 'primaryButtonBackground'),
  },
  expandCollapseButton: {
    color: get(color, 'primaryButtonBackground'),
    float: 'right',
    '&:focus': {
      outlineWidth: '1px !important',
      outlineOffset: '-4px !important',
      outlineStyle: 'solid !important',
      outlineRadius: '3px !important',
    },
  },
  tableHead: {
    '& th': {
      color: `${get(color, 'bodyText')} !important`,
      fontFamily: `${get(typography, 'bodyText')} !important`,
      fontSize: `${get(fontSize, 'bodyText')} !important`,
      fontWeight: 'bold !important',
      cursor: 'auto',
    },
  },
  generateSecretContainer: {
    display: 'flex',
  },
  generateSecretMenuButton: {
    border: `1px solid ${get(color, 'borderColor')}`,
    color: get(color, 'error'),
    '&:hover': {
      color: get(color, 'error'),
    },
    '&:focus': {
      border: `1px solid ${get(color, 'error')}`,
    },
    height: 'fit-content',
  },
  paginationContainer: {
    padding: '0px 36px',
    margin: '0 !important',
    '& .MuiTablePagination-toolbar': {
      margin: '0 !important',
    },
  },
  singleKeyViewContainer: {
    backgroundColor: `${get(color, 'uiBackground')} !important`,
  },
  addKeyButton: {
    marginLeft: 'auto',
    marginTop: 30,
    marginBottom: 30,
    marginRight: 50,
    '&:focus': {
      outlineWidth: '1px !important',
      outlineOffset: '-4px !important',
      outlineStyle: 'solid !important',
      outlineRadius: '3px !important',
      outlineColor: get(color, 'primaryButtonBackground'),
    },
  },
  registerKeyButton: {
    marginTop: 64,
    width: 200,
  },
  filtersAndAdd: {
    float: 'right',
  },
  singleKeyViewCell: {
    padding: 36,
    backgroundColor: convertHexToRGB(get(color, 'borderColor'), 0.3),
  },
  defaultChip: {
    backgroundColor: `${convertHexToRGB(get(color, 'bodyText'), 0.05)} !important`,
    color: `${get(color, 'bodyText')} !important`,
    fontSize: `${get(fontSize, 'smallText')} !important`,
    margin: '0 10px',
    height: '24px',
  },
  enabledChip: {
    backgroundColor: `${convertHexToRGB(get(color, 'success'))} !important`,
    color: `${get(color, 'white')} !important`,
    fontSize: `${get(fontSize, 'smallText')} !important`,
    margin: '0 10px',
    height: '24px',
  },
  disabledChip: {
    backgroundColor: `${convertHexToRGB(get(color, 'error'))} !important`,
    color: `${get(color, 'white')} !important`,
    fontSize: `${get(fontSize, 'smallText')} !important`,
    margin: '0 10px',
    height: '24px',
  },
  oauthPanel: {
    backgroundColor: `${get(color, 'uiBackground')} !important`,
    width: '80%',
    padding: 36,
    '& a': {
      color: get(color, 'link'),
    },
    wordBreak: 'break-all',
  },
  oauthPanelItemContainer: {
    marginTop: '10px !important',
    marginBottom: '10px !important',
  },
  oauthPanelTitle: {
    backgroundColor: 'transparent !important',
    color: get(color, 'bodyText'),
    fontFamily: get(typography, 'bodyText'),
    fontSize: get(fontSize, 'bodyText'),
    fontWeight: 'bold !important',
    outline: 'transparent !important',
    '-webkit-font-smoothing': 'antialiased',
    lineHeight: '21px !important',
  },
  oauthPanelDesc: {
    backgroundColor: 'transparent !important',
    lineHeight: '21px !important',
    color: get(color, 'bodyText'),
    fontFamily: get(typography, 'bodyText'),
    fontSize: get(fontSize, 'bodyText'),
    outline: 'transparent !important',
    '-webkit-font-smoothing': 'antialiased',
  },
  tableContainer: {
    overflowY:'hidden',
  },
  copyButton: {
    color: get(color, 'primaryButtonBackground'),
    marginTop: 51,
    '&:focus': {
      outlineWidth: '1px !important',
      outlineOffset: '-4px !important',
      outlineStyle: 'solid !important',
      outlineRadius: '3px !important',
      outlineColor: get(color, 'borderColor'),
    },
  },
  editKeyButton: {
    color: get(color, 'primaryButtonBackground'),
    '&:focus': {
      outlineWidth: '1px !important',
      outlineOffset: '-4px !important',
      outlineStyle: 'solid !important',
      outlineRadius: '3px !important',
      outlineColor: get(color, 'borderColor'),
    },
  },
  forceDeleteButton: {
    backgroundColor: get(color, 'error'),
    '&:hover': {
      backgroundColor: get(color, 'errorHover'),
    },
    '&:focus': {
      backgroundColor: get(color, 'error'),
      outlineWidth: '1px !important',
      outlineOffset: '-4px !important',
      outlineStyle: 'solid !important',
      outlineRadius: '3px !important',
      outlineColor: get(color, 'error'),
    },
  },
  fieldCopyContainer: {
    display: 'flex',
    width: '100%',
  },
  secretInfoWarningIcon: {
    marginRight: 6,
  },
  secretHelpText: {
    backgroundColor: 'rgba(255, 148, 77, 0.1)',
    border: '1px solid #FF944D',
    borderRadius: 3,
    display: 'inline-flex',
    paddingBottom: 19,
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 16,
    width: 468,
  },
  textField: {
    width: '40%',
  },
  dialogTextField: {
    width: '90%',
  },
  keyPanel: {
    padding: 36,
  },
  flexBlock: {
    display: 'flex',
  },
  lockIcon: {
    fill: convertHexToRGB(get(color, 'bodyText'), 0.6),
    marginLeft: 5,
  },
  warningIcon: {
    fill: get(color, 'warning'),
    marginLeft: 5,
  },
  lockIcon2: {
    fill: convertHexToRGB(get(color, 'bodyText'), 0.6),
    marginLeft: 5,
    marginTop: 5,
  },
  warningIcon2: {
    fill: get(color, 'warning'),
    marginLeft: 5,
    marginTop: 5,
  },
  titleAndEdit: {
    display: 'flex',
  },
  buttonContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    verticalAlign: 'middle',
    marginTop: 50,
  },
  status: {
    position: 'relative',
    display: 'inline-block',
    paddingLeft: '15px',
    verticalAlign: 'middle',
    letterSpacing: 0,
    fontSize: `${get(fontSize, 'bodyText')} !important`,
    '&:before': {
      display: 'inline-block',
      position: 'absolute',
      content: '""',
      left: 0,
      top: '50%',
      marginTop: '-6px',
      height: '10px',
      width: '10px',
      borderRadius: '10px',
    },
  },
  enabled: {
    '&:before': {
      backgroundColor: get(color, 'success'),
    },
  },
  disabled: {
    '&:before': {
      backgroundColor: get(color, 'disabledBackground'),
    },
  },
  deleteFailed: {
    '&:before': {
      backgroundColor: get(color, 'error'),
    },
  },
  normalKey: {
    '&:before': {
      backgroundColor: get(color, 'success'),
    },
  },
  startReportingKey: {
    '&:before': {
      backgroundColor: get(color, 'warning'),
    },
  },
  urgentReportingKey: {
    '&:before': {
      backgroundColor: get(color, 'error'),
    },
  },
  pendingRegistrationKey: {
    '&:before': {
      backgroundColor: get(color, 'warning'),
    },
  },
  certsTable: {
    '& th': {
      cursor: 'auto !important',
      '&:first-child' : {
        color: get(color, 'bodyText'),
      },
      borderBottom: `2px solid ${get(color, 'borderColor')}`,
    },
    '& td': {
      borderBottom: `1px solid ${get(color, 'borderColor')}`,
    },
  },
  expiryInfoSection: {
    marginTop: 50,
  },
  helpIcon: {
    fontSize: 12,
    margin: get(margin, 'm4'),
    marginTop: get(margin, 'm0'),
    verticalAlign: 'middle',
    cursor: 'pointer',
    color: `${get(color, 'link')} !important`,
  },
  pendingApiKey: {
    display: 'inline-grid',
  },
  metaDataAccordion: {
    display: 'flex',
    maxHeight: '50vh',
    overflow: 'scroll',
  },
  registrationInfoPaper: {
    padding: 32,
    paddingTop: 0,
    height: 526,
    maxHeight: 'calc(100% - 200px)',
    width: 560,
  },
});

export default styles;
